import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Cover from './Cover';
import './HeroVideo.css';

gsap.registerPlugin(ScrollTrigger);

const HERO_VIDEO_SRC = '/albums/hero/heroVideo.mp4';
const HERO_FALLBACK_SRC = '/albums/hero/hero1.webp';
const MOBILE_MAX = 768;

/** Pin scroll distance - longer feels more cinematic on desktop */
const SCRUB_DISTANCE = 2800;

/** Progress 0 → VIDEO_END_AT: scrub the film. After that: hold final frame + reveal copy. */
const VIDEO_END_AT = 0.78;
/** Copy/CTAs only after the film is fully scrubbed */
const COVER_FADE_START = 0.86;

/**
 * Desktop: Apple-style scroll-scrubbed hero → storyteller copy on the final video frame.
 * Mobile: image carousel Cover only (no video scrub).
 */
const HeroVideo = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const mediaLayerRef = useRef(null);
  const vignetteRef = useRef(null);
  const coverLayerRef = useRef(null);
  const coverActiveRef = useRef(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false
  );
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [coverActive, setCoverActive] = useState(false);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_MAX);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Mobile: images-only Cover - no video / pin / scrub
  useEffect(() => {
    if (!isMobile) return undefined;
    setIsReady(true);
    setHasError(false);
    setCoverActive(true);
    coverActiveRef.current = true;
    return undefined;
  }, [isMobile]);

  // Desktop: scroll-scrub video, then fade in text/CTAs on the final frame
  useEffect(() => {
    if (isMobile) return undefined;

    const section = sectionRef.current;
    const video = videoRef.current;
    const coverLayer = coverLayerRef.current;
    const vignette = vignetteRef.current;
    if (!section || !video) return undefined;

    let ctx;
    let cancelled = false;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      video.pause();
      if (mediaLayerRef.current) mediaLayerRef.current.style.opacity = '1';
      if (vignette) vignette.style.opacity = '0';
      if (coverLayer) {
        coverLayer.style.opacity = '1';
        coverLayer.style.pointerEvents = 'auto';
      }
      setCoverActive(true);
      setIsReady(true);
      return undefined;
    }

    const applyPhase = (progress) => {
      const duration = video.duration;
      if (!duration || !Number.isFinite(duration)) return;

      const videoProgress = Math.min(1, progress / VIDEO_END_AT);
      const targetTime = videoProgress * duration;
      if (Math.abs(video.currentTime - targetTime) > 0.04) {
        try {
          video.currentTime = targetTime;
        } catch {
          // Ignore seek race before frame is cached
        }
      }

      // Keep the video layer fully visible - hold final frame (no hero1 still)
      if (mediaLayerRef.current) {
        mediaLayerRef.current.style.opacity = '1';
      }

      // Storyteller copy + CTAs only after video scroll
      const coverOpacity = gsap.utils.clamp(
        0,
        1,
        (progress - COVER_FADE_START) / (1 - COVER_FADE_START)
      );
      if (coverLayer) {
        coverLayer.style.opacity = String(coverOpacity);
        coverLayer.style.pointerEvents = coverOpacity > 0.85 ? 'auto' : 'none';
      }
      if (vignette) {
        vignette.style.opacity = String(1 - coverOpacity);
      }

      const nextActive = coverOpacity > 0.85;
      if (nextActive !== coverActiveRef.current) {
        coverActiveRef.current = nextActive;
        setCoverActive(nextActive);
      }
    };

    const setupScrollScrub = () => {
      if (cancelled || !video.duration || !Number.isFinite(video.duration)) {
        return;
      }

      video.pause();
      video.currentTime = 0;

      ctx = gsap.context(() => {
        if (coverLayer) {
          gsap.set(coverLayer, { opacity: 0 });
        }
        coverActiveRef.current = false;
        setCoverActive(false);

        ScrollTrigger.create({
          trigger: section,
          start: 'top top',
          end: `+=${SCRUB_DISTANCE}`,
          pin: true,
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            applyPhase(self.progress);
          },
        });

        ScrollTrigger.refresh();
        setIsReady(true);
      }, section);
    };

    const onLoadedMetadata = () => {
      setupScrollScrub();
    };

    const onError = () => {
      setHasError(true);
      setIsReady(true);
      if (coverLayer) {
        coverLayer.style.opacity = '1';
        coverLayer.style.pointerEvents = 'auto';
      }
      setCoverActive(true);
    };

    if (video.readyState >= 1 && video.duration) {
      onLoadedMetadata();
    } else {
      video.addEventListener('loadedmetadata', onLoadedMetadata);
      video.addEventListener('error', onError);
      try {
        video.load();
      } catch {
        // no-op
      }
    }

    const onResize = () => {
      ScrollTrigger.refresh();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('error', onError);
      window.removeEventListener('resize', onResize);
      if (ctx) ctx.revert();
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.trigger === section) {
          trigger.kill();
        }
      });
    };
  }, [isMobile]);

  // Mobile: classic image hero
  if (isMobile) {
    return (
      <section
        className="hero-video hero-video--mobile-images is-ready cover-active"
        aria-label="Wedding photography cover"
      >
        <Cover embedded active showCarousel />
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={`hero-video${isReady ? ' is-ready' : ''}${hasError ? ' has-error' : ''}${
        coverActive ? ' cover-active' : ''
      }`}
      aria-label="Cinematic wedding introduction"
    >
      <div className="hero-video__stage">
        {/* Fallback still only when video fails - never shown after a successful scrub */}
        {hasError && (
          <img
            className="hero-video__still"
            src={HERO_FALLBACK_SRC}
            alt=""
            aria-hidden="true"
            decoding="async"
          />
        )}

        <div ref={mediaLayerRef} className="hero-video__media">
          <video
            ref={videoRef}
            className="hero-video__player"
            src={HERO_VIDEO_SRC}
            muted
            playsInline
            preload="auto"
            controls={false}
            disablePictureInPicture
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>

        <div ref={vignetteRef} className="hero-video__vignette" aria-hidden="true" />

        {/* Text + CTAs on the final video frame - no hero1 / photo carousel */}
        <div
          ref={coverLayerRef}
          className={`hero-video__cover${coverActive ? ' is-interactive' : ''}`}
          aria-hidden={!coverActive}
        >
          <Cover embedded active={coverActive} showCarousel={false} />
        </div>

        {!isReady && (
          <div className="hero-video__loader" aria-live="polite">
            <span className="hero-video__loader-dot" />
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroVideo;
